import { useEffect } from 'react';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { getFileUrl, type TicketFile } from '../services/api';
import { AuthedImage } from './AuthedImage';
import styles from './VisorImagenes.module.css';

/**
 * Caja de luz para las capturas de UN ticket, en el orden en que llegaron.
 *
 * Desde el paso 34 no hay galería: las capturas van dentro de la burbuja del
 * mensaje que las trajo, y esto es lo que se abre al pulsar una. Recorre todas
 * las del ticket (las que quien mira puede ver: al cliente el backend no le
 * manda las de una nota interna), porque comparar «antes» y «después» es
 * justo para lo que se navega.
 */
interface Props {
  incidenciaId: string;
  token: string;
  imagenes: TicketFile[];
  indice: number;
  onIndice: (i: number) => void;
  onCerrar: () => void;
}

export function VisorImagenes({ incidenciaId, token, imagenes, indice, onIndice, onCerrar }: Props) {
  const n = imagenes.length;
  const actual = imagenes[Math.min(indice, n - 1)];

  // Teclado: Escape cierra y las flechas pasan. Con `onIndice` funcional
  // desde fuera no hace falta: aquí `indice` ya es el del último pintado.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
      else if (e.key === 'ArrowLeft' && n > 1) onIndice((indice + n - 1) % n);
      else if (e.key === 'ArrowRight' && n > 1) onIndice((indice + 1) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [indice, n, onIndice, onCerrar]);

  if (!actual) return null;
  return (
    <div className={styles.overlay} onClick={onCerrar} role="dialog" aria-label="Captura ampliada">
      <button
        type="button"
        className={styles.close}
        onClick={(e) => { e.stopPropagation(); onCerrar(); }}
        aria-label="Cerrar"
      >
        <X size={20} weight="bold" />
      </button>
      {n > 1 && (
        <>
          <button
            type="button"
            className={`${styles.nav} ${styles.prev}`}
            onClick={(e) => { e.stopPropagation(); onIndice((indice + n - 1) % n); }}
            aria-label="Anterior"
          >
            <CaretLeft size={24} weight="bold" />
          </button>
          <button
            type="button"
            className={`${styles.nav} ${styles.next}`}
            onClick={(e) => { e.stopPropagation(); onIndice((indice + 1) % n); }}
            aria-label="Siguiente"
          >
            <CaretRight size={24} weight="bold" />
          </button>
        </>
      )}
      <AuthedImage
        src={getFileUrl(incidenciaId, actual.id)}
        type={actual.mediaType}
        token={token}
        alt={actual.filename}
        className={styles.img}
        onClick={(e) => e.stopPropagation()}
      />
      <span className={styles.pie}>
        {actual.filename}{n > 1 ? ` · ${indice + 1} / ${n}` : ''}
      </span>
    </div>
  );
}
